// Query strings for the admin Evals API, and the page URL state they come
// from (task eval_admin_runs). The run list's and the case table's filters
// live in the page URL, so a filtered view is a link an admin can share and the
// back button restores; these turn that URL into the API's query and back.
// Pure: tested in query.test.ts.

import type {
  EvalCaseFilter,
  EvalLaunchedBy,
  EvalRole,
  EvalRunFilter,
  EvalRunKind,
  EvalRunStatus,
  EvalSplit,
  EvalSuite,
  EvalSurface,
} from './types';

const SUITES: readonly EvalSuite[] = ['short', 'long'];
const SURFACES: readonly EvalSurface[] = ['attack', 'helpful', 'question', 'condition'];
const SPLITS: readonly EvalSplit[] = ['train', 'test'];
const LAUNCHERS: readonly EvalLaunchedBy[] = ['admin', 'cli', 'schedule'];
const KINDS: readonly EvalRunKind[] = ['collect', 'regrade', 'calibration'];
export const EVAL_RUN_STATUSES: readonly EvalRunStatus[] = [
  'queued',
  'running',
  'succeeded',
  'failed',
  'incomplete',
  'budget_exceeded',
  'cancelling',
  'cancelled',
];

/** The page size every Evals list asks for. */
export const EVAL_PAGE_SIZE = 50;

function oneOf<T extends string>(list: readonly T[], v: string | null): T | undefined {
  return v !== null && (list as readonly string[]).includes(v) ? (v as T) : undefined;
}

/** A role's pattern, as the server checks it (cloud TOM-536, ADR 0342 §1): any subject's. */
export const EVAL_ROLE_PATTERN = /^[a-z][a-z_]{0,39}$/;

function role(v: string | null): EvalRole | undefined {
  return v !== null && EVAL_ROLE_PATTERN.test(v) ? v : undefined;
}

function text(v: string | null, max: number): string | undefined {
  if (v === null) return undefined;
  const t = v.trim();
  return t.length > 0 && t.length <= max ? t : undefined;
}

function flag(v: string | null): boolean | undefined {
  if (v === 'true' || v === '1') return true;
  if (v === 'false' || v === '0') return false;
  return undefined;
}

function offset(v: string | null): number | undefined {
  if (v === null) return undefined;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 && n <= 1_000_000 ? n : undefined;
}

/** "?a=1&b=2" from the defined entries, in order; '' when there are none. */
function toQuery(entries: [string, string | number | boolean | undefined][]): string {
  const params = new URLSearchParams();
  for (const [k, v] of entries) {
    if (v === undefined) continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

// ----- The run list -----------------------------------------------------------------

/** The run list's filter, read from the page URL. Unknown or malformed values drop out. */
export function runFilterFromSearch(search: URLSearchParams): EvalRunFilter {
  const filter: EvalRunFilter = {};
  const suite = oneOf(SUITES, search.get('suite'));
  if (suite !== undefined) filter.suite = suite;
  const model = text(search.get('model'), 200);
  if (model !== undefined) filter.model = model;
  const subject = role(search.get('role'));
  if (subject !== undefined) filter.role = subject;
  const status = oneOf(EVAL_RUN_STATUSES, search.get('status'));
  if (status !== undefined) filter.status = status;
  const launchedBy = oneOf(LAUNCHERS, search.get('launchedBy'));
  if (launchedBy !== undefined) filter.launchedBy = launchedBy;
  // A `+` typed into the URL by hand reads as a space, and no version holds one
  // (`v61+3f9a2c1d`, cloud ADR 0332). A bare label matches its every version.
  const promptVersion = text(search.get('promptVersion'), 40)?.replace(/ /g, '+');
  if (promptVersion !== undefined) filter.promptVersion = promptVersion;
  const baseline = flag(search.get('baseline'));
  if (baseline !== undefined) filter.baseline = baseline;
  const runKind = oneOf(KINDS, search.get('runKind'));
  if (runKind !== undefined) filter.runKind = runKind;
  const off = offset(search.get('offset'));
  if (off !== undefined) filter.offset = off;
  return filter;
}

/** The query GET /v1/admin/evals/runs takes for a filter. */
export function runFilterQuery(filter: EvalRunFilter): string {
  return toQuery([
    ['suite', filter.suite],
    ['model', filter.model],
    ['role', filter.role],
    ['status', filter.status],
    ['launchedBy', filter.launchedBy],
    ['promptVersion', filter.promptVersion],
    ['baseline', filter.baseline],
    ['runKind', filter.runKind],
    ['limit', filter.limit ?? EVAL_PAGE_SIZE],
    ['offset', filter.offset !== undefined && filter.offset > 0 ? filter.offset : undefined],
  ]);
}

/** The page URL's search for a filter (no page size: the page decides it). */
export function runFilterSearch(filter: EvalRunFilter): string {
  return toQuery([
    ['suite', filter.suite],
    ['model', filter.model],
    ['role', filter.role],
    ['status', filter.status],
    ['launchedBy', filter.launchedBy],
    ['promptVersion', filter.promptVersion],
    ['baseline', filter.baseline],
    ['runKind', filter.runKind],
    ['offset', filter.offset !== undefined && filter.offset > 0 ? filter.offset : undefined],
  ]);
}

// ----- A run's cases ----------------------------------------------------------------

/** The case table's filter, read from the run page's URL. */
export function caseFilterFromSearch(search: URLSearchParams): EvalCaseFilter {
  const filter: EvalCaseFilter = {};
  const model = text(search.get('model'), 200);
  if (model !== undefined) filter.model = model;
  const subject = role(search.get('role'));
  if (subject !== undefined) filter.role = subject;
  if (flag(search.get('failures')) === true) filter.failures = true;
  if (flag(search.get('flaky')) === true) filter.flaky = true;
  if (flag(search.get('judgeUnstable')) === true) filter.judgeUnstable = true;
  if (flag(search.get('incomplete')) === true) filter.incomplete = true;
  const surface = oneOf(SURFACES, search.get('surface'));
  if (surface !== undefined) filter.surface = surface;
  const split = oneOf(SPLITS, search.get('split'));
  if (split !== undefined) filter.split = split;
  const q = text(search.get('q'), 200);
  if (q !== undefined) filter.q = q;
  const off = offset(search.get('offset'));
  if (off !== undefined) filter.offset = off;
  return filter;
}

function caseEntries(filter: EvalCaseFilter): [string, string | number | boolean | undefined][] {
  return [
    ['model', filter.model],
    ['role', filter.role],
    ['failures', filter.failures === true ? true : undefined],
    ['flaky', filter.flaky === true ? true : undefined],
    ['judgeUnstable', filter.judgeUnstable === true ? true : undefined],
    ['incomplete', filter.incomplete === true ? true : undefined],
    ['surface', filter.surface],
    ['split', filter.split],
    ['q', filter.q],
  ];
}

/** The query GET /v1/admin/evals/runs/:id/results takes for the case table. */
export function caseFilterQuery(filter: EvalCaseFilter): string {
  return toQuery([
    ...caseEntries(filter),
    ['caseId', filter.caseId],
    ['detail', filter.detail === true ? true : undefined],
    ['limit', filter.limit ?? EVAL_PAGE_SIZE],
    ['offset', filter.offset !== undefined && filter.offset > 0 ? filter.offset : undefined],
  ]);
}

/** The run page URL's search for a case filter. */
export function caseFilterSearch(filter: EvalCaseFilter): string {
  return toQuery([
    ...caseEntries(filter),
    ['offset', filter.offset !== undefined && filter.offset > 0 ? filter.offset : undefined],
  ]);
}

/** The drawer's one-case query: the transcripts and the deployed definition. */
export function caseDetailQuery(c: { model: string; role: EvalRole; caseId: string }): string {
  return caseFilterQuery({ model: c.model, role: c.role, caseId: c.caseId, detail: true });
}

/** True when the filter narrows the case table at all (paging aside). */
export function caseFilterActive(filter: EvalCaseFilter): boolean {
  return caseEntries(filter).some(([, v]) => v !== undefined);
}

// ----- Links --------------------------------------------------------------------------

export function runHref(id: string): string {
  return `/admin/evals/runs/${encodeURIComponent(id)}`;
}

/** Compare run a with run b, or with each model's pinned baseline when b is absent. */
export function compareHref(a: string, b?: string | null): string {
  return `/admin/evals/compare${toQuery([
    ['a', a],
    ['b', b ?? undefined],
  ])}`;
}

/** The API query for a comparison. */
export function compareQuery(a: string, b?: string | null): string {
  return toQuery([
    ['a', a],
    ['b', b ?? undefined],
  ]);
}

/** Pages a list's offset can move to. Null when there is no such page. */
export function pageOffsets(
  total: number,
  offset: number,
  limit: number
): { prev: number | null; next: number | null; from: number; to: number } {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(total, offset + limit);
  return {
    prev: offset > 0 ? Math.max(0, offset - limit) : null,
    next: offset + limit < total ? offset + limit : null,
    from,
    to,
  };
}
